<template>
  <basic-dialog
    :show="show"
    @close="$emit('close')"
    max-width="1100px"
    draggable
  >
    <template v-slot:title> <v-icon>mdi-magnify</v-icon>Find Service </template>
    <v-row align="baseline" class="pt-2" dense>
      <v-col cols="12" sm="4" class="py-0 pt-2">
        <destination-list-select
          :disabled="$apollo.loading"
          v-model="selectedDestinationId"
          :outlined="outlinedInputs"
        ></destination-list-select>
      </v-col>
      <v-col cols="12" sm="8" class="py-0 pt-2">
        <v-radio-group
          v-model="selectedSupplierType"
          :disabled="$apollo.loading"
          row
        >
          <v-radio
            v-for="sType in supplierTypes"
            :key="sType"
            :label="sType"
            :value="sType"
          ></v-radio>
        </v-radio-group>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12" class="py-0">
        <item-quick-add-split-search
          v-if="splitView && selectedSupplierType != 'TEMPLATE'"
          :items="items"
          :supplier-type-icon="supplierTypeIcon(selectedSupplierType)"
          @merge="splitView = false"
          v-model="selectedItem"
          @input="onChange"
          @unpriced-supplier="onUnpricedSupplier"
          @unpriced-service="onUnpricedService"
          @supplier-search-input="onSupplierSearchInput"
          @supplier-edit="onSupplierDetails"
          :loading="$apollo.loading"
        ></item-quick-add-split-search>
        <v-autocomplete
          v-else
          ref="service-select"
          :items="
            selectedSupplierType != 'TEMPLATE' ? groupedBySupplier : items
          "
          v-model="selectedItem"
          :label="inputLabel"
          item-text="label"
          item-value="id"
          @change="onChange"
          focusable="true"
          :loading="$apollo.loading"
          :prepend-inner-icon="supplierTypeIcon(selectedSupplierType)"
          :search-input.sync="itemSearch"
          @keyup.enter="onItemSearchEnter"
          :append-outer-icon="
            selectedSupplierType != 'TEMPLATE'
              ? 'mdi-arrow-split-vertical'
              : null
          "
          @click:append-outer="onSplitView"
          clearable
          :outlined="outlinedInputs"
        >
          <template
            v-slot:prepend-item
            v-if="selectedSupplierType != 'TEMPLATE'"
          >
            <v-list-item v-if="itemSearch != '' && itemSearch != undefined">
              <v-list-item-content>
                <v-list-item-title>
                  Press
                  <kbd @click="onItemSearchEnter">enter</kbd> to add unpriced
                  item: " <strong>{{ itemSearch }}</strong
                  >"
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

          <template v-slot:item="data">
            <template v-if="selectedSupplierType == 'TEMPLATE'">
              <v-list-item-content>
                <v-list-item-title v-html="data.item.label"></v-list-item-title>
                <v-list-item-subtitle
                  v-html="data.item.description"
                ></v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn small text icon color="indigo">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" color="grey lighten-1"
                        >mdi-information</v-icon
                      >
                    </template>
                    <template-items-grid
                      v-if="data.item.items"
                      v-model="data.item.items.edges"
                    ></template-items-grid>
                  </v-tooltip>
                </v-btn>
              </v-list-item-action>
            </template>
            <template v-else>
              <v-list-item-avatar>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-img
                      v-on="on"
                      :src="
                        data.item.service && data.item.service.imageURL != ''
                          ? data.item.service.imageURL
                          : data.item.supplier
                          ? data.item.supplier.imageURL
                          : null
                      "
                    ></v-img>
                  </template>
                  <v-container style="max-width: 500px">
                    <v-row>
                      <v-col cols="12">
                        <v-img
                          contain
                          :src="
                            data.item.service &&
                            data.item.service.imageURL != ''
                              ? data.item.service.imageURL
                              : data.item.supplier.imageURL
                          "
                        ></v-img>
                      </v-col>
                      <v-col cols="12">
                        <div v-html="data.item.supplier.description"></div>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-tooltip>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-subtitle
                  v-html="
                    `${data.item.supplierName} (${data.item.supplier.code})`
                  "
                ></v-list-item-subtitle>
                <v-list-item-title
                  v-html="
                    `${data.item.serviceName} (Price from: ${
                      data.item.priceFrom | priceLabeler
                    }${data.item.priceFromCurrencyCode} ${
                      data.item.rateTypeCode
                    })`
                  "
                ></v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn small text icon color="indigo">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" color="grey lighten-1"
                        >mdi-information</v-icon
                      >
                    </template>
                    <item-prices-grid
                      :items="[...data.item.prices]"
                    ></item-prices-grid>
                  </v-tooltip>
                </v-btn>
              </v-list-item-action>
              <v-list-item-action>
                <v-btn
                  small
                  text
                  icon
                  color="indigo"
                  @click.native.stop="onSupplierDetails(data.item, $event)"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on" small>mdi-pencil</v-icon>
                    </template>
                    Edit {{ data.item.supplierName }}
                  </v-tooltip>
                </v-btn>
              </v-list-item-action>
            </template>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>
    <v-row v-if="debug">
      <v-col>
        <code>{{ supplierType }}</code>
        <code>selected {{ selectedSupplierType }}</code>
      </v-col>
    </v-row>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../shared/BasicDialog";
import { SUPPLIER_TYPES } from "../../../../constants/supplierTypes";
import DestinationListSelect from "../../shared/DestinationListSelect";
import { SupplierTypeIconMixin } from "../../../../mixins/SupplierTypeIconMixin";
import ItemQuickAddSplitSearch from "./ItineraryBuilderItemQuickAddSplitSearch";
import ItemPricesGrid from "./ItineraryBuilderItemQuickAddItemPricesGrid";
import TemplateItemsGrid from "./ItineraryBuilderItemQuickAddTemplateItems";
import { AVAILABLE_SERVICES_SEARCH_QUERY } from "../../../../constants/graphql";
export default {
  name: "add-service-dialog",
  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    headerProps: {
      type: Object,
      default: () => ({}),
    },
    supplierType: {
      type: String,
      default: () => "ACCOMMODATION",
    },
  },

  components: {
    BasicDialog,
    DestinationListSelect,
    ItemQuickAddSplitSearch,
    ItemPricesGrid,
    TemplateItemsGrid,
  },

  mixins: [SupplierTypeIconMixin],

  data() {
    return {
      debug: false,
      splitView: false,
      selectedSupplierType: "ACCOMMODATION",
      supplierTypes: SUPPLIER_TYPES,
      selectedDestinationId: this.headerProps
        ? this.headerProps.destinationId
        : "",
      selectedItem: null,
      itemSearch: "",
      supplierNameFilter: null,
      searchTimeout: null,
      searchDebounceTime: 700,
    };
  },

  computed: {
    items() {
      return this.availableServices && this.availableServices.edges.length > 0
        ? this.availableServices.edges.map((node) => ({
            ...node.node,
            label: this.serviceLabelizer(node.node),
          }))
        : [];
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    inputLabel() {
      return this.$apollo.loading
        ? "Loading data..."
        : `Search for ${this.selectedSupplierType} (${this.items.length} results)`;
    },
    searchParams() {
      return {
        destinationId: this.selectedDestinationId,
        agentId: this.headerProps ? this.headerProps.agentId : "",
        createdAt: this.headerProps ? this.headerProps.createdAt : "",
        startDate: this.headerProps ? this.headerProps.startDate : "",
        endDate: this.headerProps ? this.headerProps.endDate : "",
        serviceType: this.selectedSupplierType,
        adults: this.headerProps ? this.headerProps.adults : 2,
        children: this.headerProps ? this.headerProps.children : 2,
        infants: this.headerProps ? this.headerProps.infants : 2,
        qty: "1",
      };
    },
    skipQuery() {
      return (
        !this.show ||
        this.selectedDestinationId == "" ||
        this.supplierTypes.indexOf(this.selectedSupplierType) < 0
      );
    },
    groupedBySupplier() {
      let supplier = "";
      const items = [];
      for (let item of this.items) {
        if (supplier != item.supplierId) {
          if (supplier != "") {
            items.push({ divider: true });
          }
          items.push({ header: item.supplierName });
          supplier = item.supplierId;
        }
        items.push(item);
      }
      return items.length > 0 ? items : [];
    },
  },

  watch: {
    headerProps(newProps) {
      if (newProps.destinationId && newProps.destinationId != "") {
        this.selectedDestinationId = newProps.destinationId;
      }
    },
    itemSearch(newVal) {
      clearTimeout(this.searchTimeout);
      if (this.selectedItem == null) {
        this.searchTimeout = setTimeout(() => {
          this.supplierNameFilter = newVal;
        }, this.searchDebounceTime);
      }
    },
    supplierType(newType) {
      this.selectedSupplierType = newType;
    },
  },

  methods: {
    onSupplierSearchInput(payload) {
      this.supplierNameFilter = payload;
    },
    onDestinationChange(payload) {
      this.selectedDestinationId = payload.id;
    },
    onChange(payload) {
      const selectedItem = this.items.find(
        (item) => item.id == this.selectedItem
      );
      if (selectedItem) {
        let selectedItemData;
        if (selectedItem.__typename == "Template") {
          selectedItemData = this.getSelectedTemplateData(selectedItem);
        } else {
          selectedItemData = this.getSelectedServiceData(selectedItem);
        }

        this.$nextTick(() => {
          this.selectedItem = null;
        });

        this.$emit("close");
        this.$emit("onSelect", selectedItemData);
      }
    },
    getSelectedTemplateData(selectedItem) {
      return { ...selectedItem };
    },
    getSelectedServiceData(selectedItem) {
      const stdOccupancy = selectedItem.service
        ? parseInt(selectedItem.service.stdOccupancy)
        : 0;
      const pax =
        parseInt(this.headerProps.adults) + parseInt(this.headerProps.children);
      const qty =
        stdOccupancy > 0 ? String(Math.ceil(pax / stdOccupancy)) : "1";
      return {
        ...selectedItem,
        startDate: this.headerProps.startDate,
        endDate: this.headerProps.endDate,
        serviceType: this.selectedSupplierType,
        price: selectedItem.priceFrom,
        currencyCode: selectedItem.priceFromCurrencyCode,
        qty,
        adults: this.headerProps.adults,
        children: this.headerProps.children,
        infants: this.headerProps.infants,
        boardBasis: selectedItem.supplier
          ? selectedItem.supplier.boardBasis
          : "",
      };
    },
    onSplitView(payload) {
      this.splitView = true;
    },
    serviceLabelizer(item) {
      return `${item.supplierName} (${item.supplier.code}) - ${
        item.serviceName
      } (Price from: ${item.priceFrom | priceLabeler}${
        item.priceFromCurrencyCode
      } ${item.rateTypeCode})`;
    },
    onSupplierDetails(item, event) {
      this.$emit("close");
      this.$emit("supplier-edit", item);
    },
    onUnpricedSupplier(event) {
      this.$emit("close");
      this.$emit("showAdhoc", {
        supplierType: this.selectedSupplierType,
        destinationId: this.selectedDestinationId,
        supplierName: event,
      });
    },
    onUnpricedService(event) {
      this.$emit("close");
      this.$emit("showAdhoc", {
        supplierType: this.selectedSupplierType,
        destinationId: this.selectedDestinationId,
        ...event,
      });
    },
    onItemSearchEnter(event) {
      this.$emit("showAdhoc", {
        supplierType: this.selectedSupplierType,
        destinationId: this.selectedDestinationId,
        supplierName: this.itemSearch,
      });
      this.$emit("close");
    },
    onSupplierSearchInput(payload) {
      this.supplierNameFilter = payload;
    },
  },

  apollo: {
    availableServices: {
      query: AVAILABLE_SERVICES_SEARCH_QUERY,
      variables() {
        return {
          destinationId: this.selectedDestinationId,
          agentId: this.headerProps ? this.headerProps.agentId : "",
          startDate: this.headerProps ? this.headerProps.startDate : "",
          endDate: this.headerProps ? this.headerProps.endDate : "",
          serviceType: this.selectedSupplierType,
          adults: this.headerProps ? this.headerProps.adults : 2,
          children: this.headerProps ? this.headerProps.children : 2,
          infants: this.headerProps ? this.headerProps.infants : 2,
          supplierName: this.supplierNameFilter,
          department: this.headerProps.departmentName,
          qty: "1",
          first: 100,
        };
      },
      skip() {
        return this.skipQuery;
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/itineraries/builder/ItineraryBuilderAddServiceDialog.vue